// ─────────────────────────────────────────────────────────────────────────────
// audio_drum.ts — Wooden tap drum (paper-membrane → djembe metaphor)
//
// Imagine the page is a sheet of paper resting over an open djembe. The
// sound underneath is rich and woody, but the paper covers it — pen
// contact muffles the drum body. Lifting the pen ("releasing on time")
// uncovers the drum and lets the body resonate.
//
// Implemented as: triangle-wave body through a per-note low-pass filter.
// Cutoff is LOW at pen-down (paper covers the drum, "almost just a
// heartbeat"); pen-up snaps the cutoff up to reveal the woody body
// resonance. During hold the cutoff drifts upward gently — that drift
// IS the tap-time-as-attack-time semantic: a very quick tap snaps the
// drum from fully-muffled into fully-exposed; a longer hold lets the
// muffling soften gradually before the snap.
//
// Pressure (energy) controls:
//   - peak amplitude   (firm strike → louder)
//   - filter peak Hz   (firm strike → brighter open)
//
// Hold time (attack time) controls:
//   - filter snap rate at pen-up
//   - whether the gain envelope rings naturally or gets cut
//
// Layered voicing kept lean: a single triangle oscillator is enough
// once the lowpass filter does the EQ work. No noise puff — pen-down
// is a quiet thud, not a click.
//
// Control surface:
//   tapDown(noteId, opts?)         — fire body, filter closed
//   tapRelease(noteId, holdMs)     — open filter, schedule release
//   tapDiscard(noteId)             — cancel ASAP (gesture became stroke)
//   setEnvelope(partial)           — override default envelope ms
//   reset()                        — defaults + silence everything
// ─────────────────────────────────────────────────────────────────────────────

import type { Instrument } from './audio_instrument';
import type { AudioChannel } from './audio_channel';

export interface DrumEnvelope {
  attackMs: number;      // 0 → peak (impact rise)
  decayMs: number;       // peak → sustain (body settle)
  bodySustain: number;   // sustain level (× peak) while held
  releaseMs: number;     // sustain → silence after pen-up
  /** Peak gain at maximum pressure. Pressure (0..1) scales 0.4..1.0
   *  of this — feather taps stay audible but quieter. */
  peakGain: number;
}

const DEFAULT_ENV: DrumEnvelope = {
  attackMs:    6,
  decayMs:     90,
  bodySustain: 0.32,
  releaseMs:   260,
  peakGain:    0.55,
};

/** Wooden body fundamental (Hz). Triangle wave at this pitch through a
 *  lowpass gives a soft mid-range thud — woody, not kick-y. */
const BODY_FREQ_HZ = 220;

/** Filter cutoff (Hz) while the pen is down — paper covers the djembe,
 *  most of the body's harmonic content is muffled. */
const FILTER_MUFFLED_HZ = 480;

/** Cutoff the gradual hold-time drift converges toward. Pen-up snaps
 *  past this to FILTER_OPEN_PEAK_HZ (scaled by pressure). */
const FILTER_HOLD_DRIFT_HZ = 1400;
const FILTER_HOLD_DRIFT_SECONDS = 0.30;

/** Cutoff (Hz) at full pressure on pen-up. Pressure scales 0.5..1.0
 *  of this so a soft tap doesn't sound the same as a hard tap. */
const FILTER_OPEN_PEAK_HZ = 3600;

/** Filter snap time (s) on pen-up — fast for quick taps, longer for
 *  held strokes. Keeps the response punchy while still musical. */
const FILTER_SNAP_QUICK_S = 0.020;
const FILTER_SNAP_HELD_S  = 0.080;

/** Hold thresholds for pen-up dampening behaviour. */
const NATURAL_RING_HOLD_MS = 150;
const HARD_DAMP_HOLD_MS = 400;

interface ActiveNote {
  body: OscillatorNode;
  filter: BiquadFilterNode;
  gain: GainNode;
  pressure: number;
  startedAt: number;
}

export class DrumInstrument implements Instrument {
  private _env: DrumEnvelope = { ...DEFAULT_ENV };
  private _notes = new Map<string, ActiveNote>();

  constructor(
    private readonly _ctx: AudioContext,
    private readonly _channel: AudioChannel,
  ) {}

  // ── Control surface ────────────────────────────────────────────────

  /** Fire a note at this moment with the filter closed. The body
   *  oscillator + gain envelope are scheduled now; tapRelease later
   *  opens the filter so the drum body becomes audible.
   *
   *  Pressure (0..1) feeds two things: peak amplitude (energy of the
   *  strike) and the eventual peak filter cutoff (how bright the open
   *  drum is). Defaults to 0.6 when the stylus doesn't report. */
  tapDown(
    noteId: string,
    opts: { freq?: number; pressure?: number } = {},
  ): void {
    const ctx = this._ctx;
    const now = ctx.currentTime;
    const env = this._env;

    const pressure = clamp01(opts.pressure ?? 0.6);
    // Amplitude: 0.4..1.0 of configured peak.
    const peak = env.peakGain * (0.4 + 0.6 * pressure);
    const body0 = (opts.freq ?? BODY_FREQ_HZ);

    // Build voicing: body → filter → gain → channel
    const gain = ctx.createGain();
    gain.connect(this._channel.input);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.Q.value = 1.0;
    filter.frequency.setValueAtTime(FILTER_MUFFLED_HZ, now);
    // Drift cutoff slightly upward during hold — softens the muffle as
    // the user keeps holding, so the eventual pen-up snap doesn't feel
    // like a sudden gate opening.
    filter.frequency.linearRampToValueAtTime(
      FILTER_HOLD_DRIFT_HZ, now + FILTER_HOLD_DRIFT_SECONDS,
    );
    filter.connect(gain);

    const body = ctx.createOscillator();
    body.type = 'triangle';
    body.frequency.setValueAtTime(body0 * 1.4, now);
    body.frequency.exponentialRampToValueAtTime(body0, now + 0.04);
    body.connect(filter);
    body.start(now);

    // Gain envelope: attack → decay → sustain (held). Release is
    // scheduled by tapRelease so the body sustains as long as the user
    // holds the pen (capped by HARD_DAMP_HOLD_MS via tapRelease).
    const aS = env.attackMs / 1000;
    const dS = env.decayMs / 1000;
    const sustain = peak * env.bodySustain;

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(peak, now + aS);
    gain.gain.exponentialRampToValueAtTime(Math.max(sustain, 0.0001), now + aS + dS);

    // Safety: if the user never releases, force-stop after several
    // seconds so we don't leak the oscillator.
    body.stop(now + 5);

    this._notes.set(noteId, { body, filter, gain, pressure, startedAt: now });
  }

  /** Pen-up. Opens the filter (revealing the drum body) and schedules
   *  the gain release. Hold time controls how snappy the open is and
   *  whether the body rings out or gets dampened. */
  tapRelease(noteId: string, holdMs: number): void {
    const note = this._notes.get(noteId);
    if (!note) return;
    this._notes.delete(noteId);

    const ctx = this._ctx;
    const now = ctx.currentTime;
    const env = this._env;

    // Filter peak depends on pressure (0.5..1.0 of the open peak).
    const filterPeak = FILTER_OPEN_PEAK_HZ * (0.5 + 0.5 * note.pressure);
    // Snap rate depends on hold time — quick tap = fast snap, slow tap
    // = gentler reveal. The snap happens AT pen-up regardless of hold.
    const snapT = clamp01((holdMs - NATURAL_RING_HOLD_MS)
      / (HARD_DAMP_HOLD_MS - NATURAL_RING_HOLD_MS));
    const snapS = FILTER_SNAP_QUICK_S
      + (FILTER_SNAP_HELD_S - FILTER_SNAP_QUICK_S) * snapT;

    note.filter.frequency.cancelScheduledValues(now);
    note.filter.frequency.setValueAtTime(note.filter.frequency.value, now);
    note.filter.frequency.linearRampToValueAtTime(filterPeak, now + snapS);

    // Gain release: short hold = natural ring (long release), long hold
    // = aggressive cut (energy reabsorbed).
    let releaseS: number;
    if (holdMs < NATURAL_RING_HOLD_MS) {
      releaseS = env.releaseMs / 1000;
    } else if (holdMs < HARD_DAMP_HOLD_MS) {
      const t = (holdMs - NATURAL_RING_HOLD_MS) / (HARD_DAMP_HOLD_MS - NATURAL_RING_HOLD_MS);
      releaseS = (env.releaseMs / 1000) * (1 - t * 0.7);
    } else {
      releaseS = 0.06;   // hard dampening
    }

    note.gain.gain.cancelScheduledValues(now);
    note.gain.gain.setValueAtTime(note.gain.gain.value, now);
    note.gain.gain.exponentialRampToValueAtTime(0.0001, now + releaseS);

    this._stop(note, now + releaseS + 0.02);
  }

  /** Drop the note ASAP — gesture became a stroke or got cancelled.
   *  Tiny fade so we don't click off mid-cycle. */
  tapDiscard(noteId: string): void {
    const note = this._notes.get(noteId);
    if (!note) return;
    this._notes.delete(noteId);
    const now = this._ctx.currentTime;
    note.gain.gain.cancelScheduledValues(now);
    note.gain.gain.setValueAtTime(note.gain.gain.value, now);
    note.gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.025);
    this._stop(note, now + 0.04);
  }

  /** Override one or more envelope params; pass {} to no-op. */
  setEnvelope(partial: Partial<DrumEnvelope>): void {
    Object.assign(this._env, partial);
  }

  reset(): void {
    this._env = { ...DEFAULT_ENV };
    const now = this._ctx.currentTime;
    for (const [, note] of this._notes) {
      this._stop(note, now);
    }
    this._notes.clear();
  }

  // ── Internals ──────────────────────────────────────────────────────

  private _stop(note: ActiveNote, when: number): void {
    try { note.body.stop(when); } catch { /* idem */ }
  }
}

function clamp01(v: number): number { return Math.max(0, Math.min(1, v)); }
